import React, { useCallback } from 'react';

import { AIEventsInstrumentationProvider } from '@atlassian/ai-analytics/ai-events-instrumentation-provider';

import {
	useBulkIssuesList,
	useIsCreatedWithAi,
	useResetTransientBulkCreateState,
	useIsRovoPanelWidth,
} from '../../providers/BulkCreateContextProvider.tsx';
import { AiBulkNavigationContainer } from '../../ui/ai-work-create/AiBulkNavigationContainer.tsx';
import { BulkIssueCreateProvidersWrapper } from '../../providers/BulkIssueCreateProvidersWrapper.tsx';
import { useGetInitialDataForBulkIssueCreateForm } from '../../services/useGetInitialDataForBulkIssueCreateForm.tsx';
import { useHasRequiredFields } from '../../services/useHasRequiredFields.tsx';
import { useGetWarningDialogActions } from '../../utils/useGetWarningDialogActions.tsx';
import { IssueCreateWarningDialog } from '../work-create-common/IssueCreateWarningDialog.tsx';

import {
	AI_BULK_ISSUE_CREATE_FEATURE_NAME,
	useBulkCreateLoggingEvents,
} from '../../utils/useBulkCreateLoggingEvents.tsx';

import { SidePanelWidthProvider } from '../work-create-common/SidePanelWrapper.tsx';
import { BulkNavigationContainer } from './BulkNavigationContainer.tsx';

export const bulkCreateSidePanelTestId = 'bulk-issue-create-side-panel';
export const BULK_ISSUE_CREATE_SIDE_PANEL_ID = 'bulk-issue-create-side-panel';
export const BULK_ISSUE_CREATE_SIDE_PANEL_WIDTH = 320;
export const ROVO_BULK_CREATE_SIDE_PANEL_WIDTH = 439;

export const BulkCreateSidePanelComponent = ({
	onClose,
}: {
	onClose: () => void;
}): React.JSX.Element => {
	useGetInitialDataForBulkIssueCreateForm();
	const resetTransientBulkCreateState = useResetTransientBulkCreateState();
	const bulkIssuesList = useBulkIssuesList();
	const isCreatedWithAi = useIsCreatedWithAi();
	const isRovoPanelWidth = useIsRovoPanelWidth();
	useHasRequiredFields();
	const { logSidePanelClosed } = useBulkCreateLoggingEvents();

	const handleOnClose = useCallback(() => {
		logSidePanelClosed();
		void resetTransientBulkCreateState();
		onClose();
	}, [logSidePanelClosed, onClose, resetTransientBulkCreateState]);

	const { isWarningDialogOpen, handleConfirm, handleCancel } =
		useGetWarningDialogActions(handleOnClose);

	const aiBulkContent = isRovoPanelWidth ? (
		<SidePanelWidthProvider value={ROVO_BULK_CREATE_SIDE_PANEL_WIDTH}>
			<AiBulkNavigationContainer onClose={handleOnClose} />
		</SidePanelWidthProvider>
	) : (
		<AiBulkNavigationContainer onClose={handleOnClose} />
	);

	const navigationContent = isCreatedWithAi ? (
		aiBulkContent
	) : (
		<BulkNavigationContainer onClose={handleOnClose} />
	);

	return (
		<>
			{navigationContent}
			{isWarningDialogOpen && (
				<IssueCreateWarningDialog
					onCancel={handleCancel}
					onConfirm={handleConfirm}
					issuesCount={bulkIssuesList?.length}
				/>
			)}
		</>
	);
};

export const BulkCreateSidePanel = ({ onClose }: { onClose: () => void }): React.JSX.Element => {
	return (
		<BulkIssueCreateProvidersWrapper>
			<AIEventsInstrumentationProvider
				config={{
					// Required configruation
					product: 'confluence',
					subproduct: 'jira',
					aiFeatureName: AI_BULK_ISSUE_CREATE_FEATURE_NAME,
					proactiveGeneratedAI: 0,
					userGeneratedAI: 1,
					// Optional configuration
					source: 'confluenceHighlightIssueCreate',
					skipAISessionValidation: true,
				}}
			>
				<BulkCreateSidePanelComponent onClose={onClose} />
			</AIEventsInstrumentationProvider>
		</BulkIssueCreateProvidersWrapper>
	);
};
